import styled from '@emotion/styled';
import { AddressResponse } from 'TYPES/api.body';
import { MoreIcon, DeleteCartProduct as TrashIcon } from 'assets/Icons';
import { apiResponse } from 'network/config/message.constants';
import { deleteAddress, setDefaultAddress } from 'network/services/address';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';
import { contactState } from 'store/atoms/contactAtom';
import { DropDown } from '..';
import { DropCON } from './styles';

// New styled components for redesigned card
const ModernAddressCard = styled.div<{ $isDefault: boolean }>`
  position: relative;
  width: 100%; // Take full width
  background: ${props => (props.$isDefault ? '#f8f9ff' : '#ffffff')};
  border: 1px solid ${props => (props.$isDefault ? '#5c6ac4' : '#e0e0e0')};
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  ${props =>
    props.$isDefault &&
    `
    &:before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 5px;
      height: 100%;
      background: #5c6ac4;
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }
  `}
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DefaultBadge = styled.div`
  background: #e8eaff;
  color: #5c6ac4;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;

  &:before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #5c6ac4;
    border-radius: 50%;
    margin-right: 5px;
  }
`;

const StreetAddress = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
  width: 100%; // Ensure the street address takes full width
  overflow-wrap: break-word; // Ensure long addresses wrap properly
  word-break: break-word;
`;

const LocationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap; // Allow wrapping on small screens
  margin-bottom: 12px;
  font-size: 13px;
  color: #666;
  width: 100%;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
`;

const RegionInfo = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  &:before {
    content: '📍';
    margin-right: 5px;
    font-size: 14px;
    flex-shrink: 0;
  }
`;

const AddressCode = styled.span`
  background: #f0f0f0;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: #666;
  font-weight: 500;
`;

const PhoneNumber = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #666;

  &:before {
    content: '📱';
    margin-right: 5px;
    font-size: 14px;
  }
`;

const ActionButton = styled.div`
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.2s;
  border-radius: 4px;
  font-size: 13px;

  &:hover {
    background: #f5f5f5;
  }
`;

const ActionsMenu = styled(DropCON)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px;
`;

interface Props {
  address: AddressResponse;
  onEdit?: (address: AddressResponse) => void;
  // new optional props
  onDelete?: () => Promise<void> | void;
  deleting?: boolean;
}

export const AddressCard: React.FC<Props> = ({ address, onEdit, onDelete, deleting }) => {
  const setContactInfo = useSetRecoilState(contactState);
  const { street, phoneNumber, status, state, country, address_code } = address;
  const queryClient = useQueryClient();

  // keep internal delete helper for standalone usage (not used if parent provides onDelete)
  const _deleteInternal = async () => {
    try {
      const { data } = await deleteAddress(`${address.id}`);
      apiResponse(data, 'success');
      queryClient.invalidateQueries('addresses');
    } catch (error) {
      enqueueSnackbar(`Error deleting address: ${(error as any).message}`, { variant: 'error' });
    }
  };

  const _edit = () => {
    if (onEdit) return onEdit(address);
    setContactInfo({ isEditAddressModal: true, ...address, id: `${address.id}`, city: address.city ?? '' });
  };

  const saveAsDefault = async () => {
    if (status) return;
    try {
      await setDefaultAddress(`${address.id}`);
      queryClient.invalidateQueries('addresses');
    } catch (error) {
      enqueueSnackbar(`Error setting default address: ${(error as any).message}`, { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      await onDelete();
    } else {
      await _deleteInternal();
    }
  };

  return (
    <ModernAddressCard $isDefault={status}>
      <CardHeader>
        {status && <DefaultBadge>Default</DefaultBadge>}
        <DropDown x="right" label={<MoreIcon />}>
          <ActionsMenu>
            <ActionButton onClick={_edit}>Edit Address</ActionButton>
            {!status && <ActionButton onClick={saveAsDefault}>Set As Default</ActionButton>}
            <ActionButton onClick={handleDelete} aria-disabled={deleting} role="button">
              {deleting ? (
                <>
                  <TrashIcon width={14} />&nbsp; Deleting...
                </>
              ) : (
                <>
                  <TrashIcon width={14} />&nbsp; Delete Address
                </>
              )}
            </ActionButton>
          </ActionsMenu>
        </DropDown>
      </CardHeader>

      <StreetAddress>{street}</StreetAddress>

      <LocationInfo>
        <RegionInfo>
          {state}, {country}
        </RegionInfo>
        {address_code && <AddressCode>#{address_code}</AddressCode>}
      </LocationInfo>

      <PhoneNumber>{phoneNumber}</PhoneNumber>
    </ModernAddressCard>
  );
};